import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";

import Icon from "../Icon";
import { getBase64 } from "../../helpers/conversions";

const FORMATS = {
    jpeg: { mime: 'image/jpeg', extensions: ['.jpg', '.jpeg'], label: 'JPEG' },
    png: { mime: 'image/png', extensions: ['.png'], label: 'PNG' },
    svg: { mime: 'image/svg+xml', extensions: ['.svg'], label: 'SVG' }
};

const DEFAULT_FORMATS = ['jpeg', 'png', 'svg'];

// O filtro do seletor de arquivo, o rótulo e a mensagem de erro saem todos da
// mesma lista, para não divergirem quando um campo aceitar formatos diferentes.
const buildAccept = formats => Object.fromEntries(
    formats.map(key => [FORMATS[key].mime, FORMATS[key].extensions])
);

const buildLabel = formats => {
    const labels = formats.map(key => FORMATS[key].label);

    if (labels.length === 1) return labels[0];

    return `${labels.slice(0, -1).join(', ')} ou ${labels[labels.length - 1]}`;
};

export const InputImage = ({ label, src, hint, square, formats = DEFAULT_FORMATS, changeBase64, changeName, onRemove }) => {
    const [loading, setLoading] = useState(false);

    const acceptLabel = buildLabel(formats);

    const handleDrop = files => {
        const file = files[0];
        if (!file) return;

        setLoading(true);
        changeName?.(file.name);
        getBase64(file, res => {
            changeBase64(res);
            setLoading(false);
        });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDropAccepted: handleDrop,
        onDropRejected: () => toast.error(`Formato inválido. Use ${acceptLabel}.`),
        accept: buildAccept(formats),
        maxFiles: 1,
        noKeyboard: true,
        // O react-dropzone manda um "type" por MIME para o showOpenFilePicker, e
        // o Chrome deixa só o primeiro selecionado: a janela abria filtrando
        // apenas JPEG. Sem a File System Access API vale o accept do input, que
        // é um filtro só com todos os formatos.
        useFsAccessApi: false
    });

    const remove = e => {
        e.stopPropagation();
        changeBase64('');
        changeName?.('');
        onRemove?.();
    };

    const classes = [
        'input-image',
        square ? 'input-image--square' : '',
        src ? 'input-image--filled' : '',
        isDragActive ? 'input-image--drop-active' : ''
    ].filter(Boolean).join(' ');

    return (
        <div className="input-image-group">
            {label && <label className="field-label">{label}</label>}

            <div {...getRootProps({ className: classes })}>
                <input {...getInputProps()} />

                {loading && <Icon name="loading" size={20} className="spin" />}

                {!loading && src &&
                    <>
                        <img src={src} alt={label ?? ''} />
                        <button type="button" className="input-image__remove" aria-label="Remover imagem" onClick={remove}>
                            <Icon name="close" size={14} />
                        </button>
                    </>
                }

                {!loading && !src &&
                    <div className="input-image__empty">
                        <Icon name="upload-small" size={18} />
                        <span>{isDragActive ? 'Solte a imagem aqui' : 'Clique ou arraste uma imagem'}</span>
                        <p className="input-image__hint">
                            {acceptLabel}{hint ? ` · ${hint}` : ''}
                        </p>
                    </div>
                }
            </div>
        </div>
    );
};

export default InputImage;
